import { bigIntParser } from "../utils/functions/bigIntParser";
import { endpointGenerator } from "../utils/functions/endpointGenerator";
import { type Execution } from "../utils/functions/execution";
import { paginateEndpoint } from "../utils/functions/paginateEndpoint";
import {
    type BalancesResponse,
    type Erc20TransfersResponse,
    type GetErc20TransfersForWalletAddressQueryParamOpts,
    type GetHistoricalPortfolioForWalletAddressQueryParamOpts,
    type GetHistoricalTokenBalancesForWalletAddressQueryParamOpts,
    type GetNativeTokenBalanceQueryParamOpts,
    type GetTokenBalancesForWalletAddressQueryParamOpts,
    type GetTokenHoldersV2ForTokenAddressQueryParamOpts,
    type HistoricalBalancesResponse,
    type PortfolioResponse,
    type TokenBalanceNativeResponse,
    type TokenHoldersResponse,
} from "../utils/types/BalanceService.types";
import {
    type Chain,
    type GoldRushResponse,
} from "../utils/types/Generic.types";

/**
 * Balances APIs
 *
 */
export class BalanceService {
    constructor(private execution: Execution) {}

    /**
     *
     * Commonly used to fetch the native and fungible (ERC20) tokens held by an address. Response includes spot prices and other metadata.
     *
     * **Credit Cost**: 1 per call
     *
     * @param {Chain} chainName - The chain name eg: `eth-mainnet` or 1.
     * @param {string} walletAddress - The requested address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
     * @param {GetTokenBalancesForWalletAddressQueryParamOpts} queryParamOpts
     *   - `quoteCurrency`: The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`.
     *   - `noSpam`: If `true`, the suspected spam tokens are removed. Supports `eth-mainnet` and `matic-mainnet`.
     *
     */
    public async getTokenBalancesForWalletAddress(
        chainName: Chain,
        walletAddress: string,
        queryParamOpts?: GetTokenBalancesForWalletAddressQueryParamOpts
    ): Promise<GoldRushResponse<BalancesResponse>> {
        const endpoint = endpointGenerator(
            `${chainName}/address/${walletAddress}/balances_v2`,
            [
                {
                    key: "quote-currency",
                    value: queryParamOpts?.quoteCurrency,
                },
                {
                    key: "no-spam",
                    value: queryParamOpts?.noSpam,
                },
            ]
        );

        const parseData = (data: GoldRushResponse<BalancesResponse>) => {
            if (data.data) {
                data.data.updated_at = data.data.updated_at
                    ? new Date(data.data.updated_at)
                    : null;
                data.data.items = data.data.items
                    ? data.data.items.map((balanceItem) => ({
                          ...balanceItem,
                          balance: bigIntParser(balanceItem.balance),
                          balance_24h: bigIntParser(balanceItem.balance_24h),
                          last_transferred_at: balanceItem.last_transferred_at
                              ? new Date(balanceItem.last_transferred_at)
                              : null,
                          nft_data: balanceItem.nft_data
                              ? balanceItem.nft_data.map((nftItem) => ({
                                    ...nftItem,
                                    token_id: bigIntParser(nftItem.token_id),
                                    token_balance: bigIntParser(
                                        nftItem.token_balance
                                    ),
                                    token_price_wei: bigIntParser(
                                        nftItem.token_price_wei
                                    ),
                                }))
                              : null,
                      }))
                    : null;
            }

            return data;
        };

        return await this.execution.execute<BalancesResponse>(
            endpoint,
            parseData
        );
    }

    /**
     *
     * Commonly used to render a daily portfolio balance for an address broken down by the token. The timeframe is user-configurable, defaults to 30 days.
     *
     * **Credit Cost**: 2 per 30 days
     *
     * @param {Chain} chainName - The chain name eg: `eth-mainnet` or 1.
     * @param {string} walletAddress - The requested address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
     * @param {GetHistoricalPortfolioForWalletAddressQueryParamOpts} queryParamOpts
     *   - `quoteCurrency`: The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`.
     *   - `days`: The number of days to return data for. Defaults to 30 days.
     *
     */
    public async getHistoricalPortfolioForWalletAddress(
        chainName: Chain,
        walletAddress: string,
        queryParamOpts?: GetHistoricalPortfolioForWalletAddressQueryParamOpts
    ): Promise<GoldRushResponse<PortfolioResponse>> {
        const endpoint = endpointGenerator(
            `${chainName}/address/${walletAddress}/portfolio_v2`,
            [
                {
                    key: "quote-currency",
                    value: queryParamOpts?.quoteCurrency,
                },
                {
                    key: "days",
                    value: queryParamOpts?.days,
                },
            ]
        );

        const parseData = (data: GoldRushResponse<PortfolioResponse>) => {
            if (data.data) {
                data.data.updated_at = data.data.updated_at
                    ? new Date(data.data.updated_at)
                    : null;
                data.data.items =
                    data.data.items?.map((portfolioItem) => ({
                        ...portfolioItem,
                        holdings:
                            portfolioItem.holdings?.map((holdingItem) => ({
                                ...holdingItem,
                                timestamp: holdingItem.timestamp
                                    ? new Date(holdingItem.timestamp)
                                    : null,
                                close: {
                                    ...holdingItem.close!,
                                    balance: bigIntParser(
                                        holdingItem.close?.balance
                                    ),
                                },
                                high: {
                                    ...holdingItem.high!,
                                    balance: bigIntParser(
                                        holdingItem.high?.balance
                                    ),
                                },
                                low: {
                                    ...holdingItem.low!,
                                    balance: bigIntParser(
                                        holdingItem.low?.balance
                                    ),
                                },
                                open: {
                                    ...holdingItem.open!,
                                    balance: bigIntParser(
                                        holdingItem.open?.balance
                                    ),
                                },
                            })) || null,
                    })) || null;
            }

            return data;
        };

        return await this.execution.execute<PortfolioResponse>(
            endpoint,
            parseData
        );
    }

    /**
     *
     * Commonly used to render the transfer-in and transfer-out of a token along with historical prices from an address.
     *
     * **Credit Cost**: 0.05 per item
     *
     * @param {Chain} chainName - The chain name eg: `eth-mainnet` or 1.
     * @param {string} walletAddress - The requested address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
     * @param {GetErc20TransfersForWalletAddressQueryParamOpts} queryParamOpts
     *   - `quoteCurrency`: The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`.
     *   - `contractAddress`: The requested contract address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
     *   - `startingBlock`: The block height to start from, defaults to `0`.
     *   - `endingBlock`: The block height to end at, defaults to current block height.
     *   - `pageSize`: Number of items per page. Omitting this parameter defaults to 100.
     *   - `pageNumber`: 0-indexed page number to begin pagination.
     *
     */
    public async *getErc20TransfersForWalletAddress(
        chainName: Chain,
        walletAddress: string,
        queryParamOpts: GetErc20TransfersForWalletAddressQueryParamOpts
    ): AsyncIterable<GoldRushResponse<Erc20TransfersResponse>> {
        const endpoint = endpointGenerator(
            `${chainName}/address/${walletAddress}/transfers_v2`,
            [
                {
                    key: "quote-currency",
                    value: queryParamOpts?.quoteCurrency,
                },
                {
                    key: "contract-address",
                    value: queryParamOpts?.contractAddress,
                },
                {
                    key: "starting-block",
                    value: queryParamOpts?.startingBlock,
                },
                {
                    key: "ending-block",
                    value: queryParamOpts?.endingBlock,
                },
                {
                    key: "page-size",
                    value: queryParamOpts?.pageSize,
                },
                {
                    key: "page-number",
                    value: queryParamOpts?.pageNumber,
                },
            ]
        );

        const parseData = (data: GoldRushResponse<Erc20TransfersResponse>) => {
            if (data.data) {
                data.data.updated_at = data.data.updated_at
                    ? new Date(data.data.updated_at)
                    : null;
                data.data.items = data.data.items
                    ? data.data.items.map((ercItem) => ({
                          ...ercItem,
                          block_signed_at: ercItem.block_signed_at
                              ? new Date(ercItem.block_signed_at)
                              : null,
                          value: bigIntParser(ercItem.value),
                          fees_paid: bigIntParser(ercItem.fees_paid),
                          transfers: ercItem.transfers
                              ? ercItem.transfers.map((transferItem) => ({
                                    ...transferItem,
                                    balance: bigIntParser(transferItem.balance),
                                    block_signed_at:
                                        transferItem.block_signed_at
                                            ? new Date(
                                                  transferItem.block_signed_at
                                              )
                                            : null,
                                    delta: bigIntParser(transferItem.delta),
                                }))
                              : null,
                      }))
                    : null;
            }
            return data;
        };

        for await (const data of paginateEndpoint<Erc20TransfersResponse>(
            endpoint,
            this.execution,
            parseData,
            "pagination"
        )) {
            yield data;
        }
    }

    /**
     *
     * Commonly used to render the transfer-in and transfer-out of a token along with historical prices from an address.
     *
     * **Credit Cost**: 0.05 per item
     *
     * @param {Chain} chainName - The chain name eg: `eth-mainnet` or 1.
     * @param {string} walletAddress - The requested address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
     * @param {GetErc20TransfersForWalletAddressQueryParamOpts} queryParamOpts
     *   - `quoteCurrency`: The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`.
     *   - `contractAddress`: The requested contract address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
     *   - `startingBlock`: The block height to start from, defaults to `0`.
     *   - `endingBlock`: The block height to end at, defaults to current block height.
     *   - `pageSize`: Number of items per page. Omitting this parameter defaults to 100.
     *   - `pageNumber`: 0-indexed page number to begin pagination.
     *
     */
    public async getErc20TransfersForWalletAddressByPage(
        chainName: Chain,
        walletAddress: string,
        queryParamOpts: GetErc20TransfersForWalletAddressQueryParamOpts
    ): Promise<GoldRushResponse<Erc20TransfersResponse>> {
        const endpoint = endpointGenerator(
            `${chainName}/address/${walletAddress}/transfers_v2`,
            [
                {
                    key: "quote-currency",
                    value: queryParamOpts?.quoteCurrency,
                },
                {
                    key: "contract-address",
                    value: queryParamOpts?.contractAddress,
                },
                {
                    key: "starting-block",
                    value: queryParamOpts?.startingBlock,
                },
                {
                    key: "ending-block",
                    value: queryParamOpts?.endingBlock,
                },
                {
                    key: "page-size",
                    value: queryParamOpts?.pageSize,
                },
                {
                    key: "page-number",
                    value: queryParamOpts?.pageNumber,
                },
            ]
        );

        const parseData = (data: GoldRushResponse<Erc20TransfersResponse>) => {
            if (data.data) {
                data.data.updated_at = data.data.updated_at
                    ? new Date(data.data.updated_at)
                    : null;
                data.data.items = data.data.items
                    ? data.data.items.map((blockTxItem) => ({
                          ...blockTxItem,
                          block_signed_at: blockTxItem.block_signed_at
                              ? new Date(blockTxItem.block_signed_at)
                              : null,
                          fees_paid: bigIntParser(blockTxItem.fees_paid),
                          transfers:
                              blockTxItem.transfers?.map((transferItem) => ({
                                  ...transferItem,
                                  balance: bigIntParser(transferItem.balance),
                                  block_signed_at: transferItem.block_signed_at
                                      ? new Date(transferItem.block_signed_at)
                                      : null,
                                  delta: bigIntParser(transferItem.delta),
                              })) || null,
                          value: bigIntParser(blockTxItem.value),
                      }))
                    : null;
            }

            return data;
        };

        return await this.execution.execute<Erc20TransfersResponse>(
            endpoint,
            parseData
        );
    }

    /**
     *
     * Used to get a paginated list of current or historical token holders for a specified ERC20 or ERC721 token.
     *
     * **Credit Cost**: 0.02 per item
     *
     * @param {Chain} chainName - The chain name eg: `eth-mainnet` or 1.
     * @param {string} tokenAddress - The requested address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
     * @param {GetTokenHoldersV2ForTokenAddressQueryParamOpts} queryParamOpts
     *   - `noSnapshot`: Defaults to `false`. Set to `true` to bypass last snapshot and get the latest token holders list.
     *   - `blockHeight`: Ending block to define a block range. Omitting this parameter defaults to the latest block height.
     *   - `pageSize`: Number of items per page. Note: Currently, only values of `100` and `1000` are supported. Omitting this parameter defaults to 100.
     *   - `pageNumber`: 0-indexed page number to begin pagination.
     *   - `date`: Ending date to define a block range (YYYY-MM-DD). Omitting this parameter defaults to the current date.
     *
     */
    public async *getTokenHoldersV2ForTokenAddress(
        chainName: Chain,
        tokenAddress: string,
        queryParamOpts?: GetTokenHoldersV2ForTokenAddressQueryParamOpts
    ): AsyncIterable<GoldRushResponse<TokenHoldersResponse>> {
        const endpoint = endpointGenerator(
            `${chainName}/tokens/${tokenAddress}/token_holders_v2`,
            [
                {
                    key: "no-snapshot",
                    value: queryParamOpts?.noSnapshot,
                },
                {
                    key: "block-height",
                    value: queryParamOpts?.blockHeight,
                },
                {
                    key: "page-size",
                    value: queryParamOpts?.pageSize,
                },
                {
                    key: "page-number",
                    value: queryParamOpts?.pageNumber,
                },
                {
                    key: "date",
                    value: queryParamOpts?.date,
                },
            ]
        );

        const parseData = (data: GoldRushResponse<TokenHoldersResponse>) => {
            if (data.data) {
                data.data.updated_at = data.data.updated_at
                    ? new Date(data.data.updated_at)
                    : null;
                data.data.items = data.data.items
                    ? data.data.items.map((tokenItem) => ({
                          ...tokenItem,
                          balance: bigIntParser(tokenItem.balance),
                          total_supply: bigIntParser(tokenItem.total_supply),
                      }))
                    : null;
            }

            return data;
        };

        for await (const data of paginateEndpoint<TokenHoldersResponse>(
            endpoint,
            this.execution,
            parseData,
            "pagination"
        )) {
            yield data;
        }
    }

    /**
     *
     * Used to get a paginated list of current or historical token holders for a specified ERC20 or ERC721 token.
     *
     * **Credit Cost**: 0.02 per item
     *
     * @param {Chain} chainName - The chain name eg: `eth-mainnet` or 1.
     * @param {string} tokenAddress - The requested address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
     * @param {GetTokenHoldersV2ForTokenAddressQueryParamOpts} queryParamOpts
     *   - `noSnapshot`: Defaults to `false`. Set to `true` to bypass last snapshot and get the latest token holders list.
     *   - `blockHeight`: Ending block to define a block range. Omitting this parameter defaults to the latest block height.
     *   - `pageSize`: Number of items per page. Note: Currently, only values of `100` and `1000` are supported. Omitting this parameter defaults to 100.
     *   - `pageNumber`: 0-indexed page number to begin pagination.
     *   - `date`: Ending date to define a block range (YYYY-MM-DD). Omitting this parameter defaults to the current date.
     *
     */
    public async getTokenHoldersV2ForTokenAddressByPage(
        chainName: Chain,
        tokenAddress: string,
        queryParamOpts?: GetTokenHoldersV2ForTokenAddressQueryParamOpts
    ): Promise<GoldRushResponse<TokenHoldersResponse>> {
        const endpoint = endpointGenerator(
            `${chainName}/tokens/${tokenAddress}/token_holders_v2`,
            [
                {
                    key: "no-snapshot",
                    value: queryParamOpts?.noSnapshot,
                },
                {
                    key: "block-height",
                    value: queryParamOpts?.blockHeight,
                },
                {
                    key: "page-size",
                    value: queryParamOpts?.pageSize,
                },
                {
                    key: "page-number",
                    value: queryParamOpts?.pageNumber,
                },
                {
                    key: "date",
                    value: queryParamOpts?.date,
                },
            ]
        );

        const parseData = (data: GoldRushResponse<TokenHoldersResponse>) => {
            if (data.data) {
                data.data.updated_at = data.data.updated_at
                    ? new Date(data.data.updated_at)
                    : null;
                data.data.items = data.data.items
                    ? data.data.items.map((balanceItem) => ({
                          ...balanceItem,
                          balance: bigIntParser(balanceItem.balance),
                          total_supply: bigIntParser(balanceItem.total_supply),
                      }))
                    : null;
            }

            return data;
        };

        return await this.execution.execute<TokenHoldersResponse>(
            endpoint,
            parseData
        );
    }

    /**
     *
     * Commonly used to fetch the historical native and fungible (ERC20) tokens held by an address at a given block height or date. Response includes daily prices and other metadata.
     *
     * **Credit Cost**: 1 per call
     *
     * @param {Chain} chainName - The chain name eg: `eth-mainnet` or 1.
     * @param {string} walletAddress - The requested address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
     * @param {GetHistoricalTokenBalancesForWalletAddressQueryParamOpts} queryParamOpts
     *   - `quoteCurrency`: The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`.
     *   - `noSpam`: If `true`, the suspected spam tokens are removed. Supports `eth-mainnet` and `matic-mainnet`.
     *   - `blockHeight`: Ending block to define a block range. Omitting this parameter defaults to the latest block height.
     *   - `date`: Ending date to define a block range (YYYY-MM-DD). Omitting this parameter defaults to the current date.
     *
     */
    public async getHistoricalTokenBalancesForWalletAddress(
        chainName: Chain,
        walletAddress: string,
        queryParamOpts?: GetHistoricalTokenBalancesForWalletAddressQueryParamOpts
    ): Promise<GoldRushResponse<HistoricalBalancesResponse>> {
        const endpoint = endpointGenerator(
            `${chainName}/address/${walletAddress}/historical_balances`,
            [
                {
                    key: "quote-currency",
                    value: queryParamOpts?.quoteCurrency,
                },
                {
                    key: "no-spam",
                    value: queryParamOpts?.noSpam,
                },
                {
                    key: "block-height",
                    value: queryParamOpts?.blockHeight,
                },
                {
                    key: "date",
                    value: queryParamOpts?.date,
                },
            ]
        );

        const parseData = (
            data: GoldRushResponse<HistoricalBalancesResponse>
        ) => {
            if (data.data) {
                data.data.updated_at = data.data.updated_at
                    ? new Date(data.data.updated_at)
                    : null;
                data.data.items = data.data.items
                    ? data.data.items.map((balanceItem) => ({
                          ...balanceItem,
                          balance: bigIntParser(balanceItem.balance),
                          last_transferred_at: balanceItem.last_transferred_at
                              ? new Date(balanceItem.last_transferred_at)
                              : null,
                          nft_data: balanceItem.nft_data
                              ? balanceItem.nft_data.map((nftItem) => ({
                                    ...nftItem,
                                    token_id: bigIntParser(nftItem.token_id),
                                    token_balance: bigIntParser(
                                        nftItem.token_balance
                                    ),
                                    token_price_wei: bigIntParser(
                                        nftItem.token_price_wei
                                    ),
                                }))
                              : null,
                      }))
                    : null;
            }

            return data;
        };

        return await this.execution.execute<HistoricalBalancesResponse>(
            endpoint,
            parseData
        );
    }

    /**
     *
     * Lightweight endpoint to just get the native token balance for an EVM address.
     *
     * **Credit Cost**: 0.5 per call
     *
     * @param {Chain} chainName - The chain name eg: `eth-mainnet` or 1.
     * @param {string} walletAddress - The requested address. Passing in an `ENS`, `RNS`, `Lens Handle`, or an `Unstoppable Domain` resolves automatically.
     * @param {GetNativeTokenBalanceQueryParamOpts} queryParamOpts
     *   - `quoteCurrency`: The currency to convert. Supports `USD`, `CAD`, `EUR`, `SGD`, `INR`, `JPY`, `VND`, `CNY`, `KRW`, `RUB`, `TRY`, `NGN`, `ARS`, `AUD`, `CHF`, and `GBP`.
     *   - `blockHeight`: Ending block to define a block range. Omitting this parameter defaults to the latest block height.
     *
     */
    public async getNativeTokenBalance(
        chainName: Chain,
        walletAddress: string,
        queryParamOpts?: GetNativeTokenBalanceQueryParamOpts
    ): Promise<GoldRushResponse<TokenBalanceNativeResponse>> {
        const endpoint = endpointGenerator(
            `${chainName}/address/${walletAddress}/balances_native`,
            [
                {
                    key: "quote-currency",
                    value: queryParamOpts?.quoteCurrency,
                },
                {
                    key: "block-height",
                    value: queryParamOpts?.blockHeight,
                },
            ]
        );

        const parseData = (
            data: GoldRushResponse<TokenBalanceNativeResponse>
        ) => {
            if (data.data) {
                data.data.updated_at = data.data.updated_at
                    ? new Date(data.data.updated_at)
                    : null;
                data.data.items = data.data.items
                    ? data.data.items.map((balanceItem) => ({
                          ...balanceItem,
                          balance: bigIntParser(balanceItem.balance),
                      }))
                    : null;
            }

            return data;
        };

        return await this.execution.execute<TokenBalanceNativeResponse>(
            endpoint,
            parseData
        );
    }
}
